import {
  videoPlaybackErrorResponseSchema,
  videoPlaybackResponseSchema,
  type VideoPlaybackErrorResponse,
  type VideoPlaybackResponse,
  type VideoSelection,
} from "@meta-video/contracts";

import {
  ApiProtocolError,
  parseApiResponse,
  SessionExpiredError,
  type ApiRequestOptions,
} from "../../shared/api/client.js";

type Fetcher = typeof fetch;
type HostedPlaybackResponse = Extract<VideoPlaybackResponse, { status: "hosted" }>;

const runtimeFetch: Fetcher = (input, init) => fetch(input, init);
const MAXIMUM_CACHED_PLAYBACK_URLS = 50;
const PLAYBACK_URL_EXPIRY_SAFETY_MS = 30_000;

export interface VideoPlaybackRequestOptions extends ApiRequestOptions {
  readonly forceRefresh?: boolean;
}

export interface VideoApi {
  readonly getPlaybackUrl: (
    selection: VideoSelection,
    options?: VideoPlaybackRequestOptions,
  ) => Promise<VideoPlaybackResponse>;
}

const playbackCacheClearers = new WeakMap<VideoApi, () => void>();

export class VideoPlaybackApiError extends Error {
  constructor(
    readonly code: VideoPlaybackErrorResponse["code"],
    readonly statusCode: VideoPlaybackErrorResponse["statusCode"],
    message: string,
  ) {
    super(message);
    this.name = "VideoPlaybackApiError";
  }
}

function playbackUrlIsFresh(
  playback: HostedPlaybackResponse,
  now = Date.now(),
): boolean {
  return Date.parse(playback.expiresAt) > now + PLAYBACK_URL_EXPIRY_SAFETY_MS;
}

function prunePlaybackUrlCache(
  cache: Map<string, HostedPlaybackResponse>,
  now = Date.now(),
): void {
  for (const [cacheKey, playback] of cache) {
    if (!playbackUrlIsFresh(playback, now)) cache.delete(cacheKey);
  }
}

async function requestPlayback(
  path: string,
  signal: AbortSignal | undefined,
  fetcher: Fetcher,
): Promise<unknown> {
  const response = await fetcher(path, {
    credentials: "same-origin",
    headers: { accept: "application/json" },
    method: "GET",
    ...(signal ? { signal } : {}),
  });
  if (response.status === 401) throw new SessionExpiredError();

  let value: unknown;
  try {
    value = await response.json();
  } catch {
    throw new ApiProtocolError(
      response.ok
        ? "Video playback response was not valid JSON"
        : `Video playback API request failed (${response.status})`,
    );
  }
  if (!response.ok) {
    const parsed = videoPlaybackErrorResponseSchema.safeParse(value);
    if (!parsed.success || parsed.data.statusCode !== response.status) {
      throw new ApiProtocolError(`Video playback API request failed (${response.status})`);
    }
    throw new VideoPlaybackApiError(
      parsed.data.code,
      parsed.data.statusCode,
      parsed.data.message,
    );
  }
  return value;
}

export function createVideoApi(fetcher: Fetcher = runtimeFetch): VideoApi {
  const playbackUrlCache = new Map<string, HostedPlaybackResponse>();
  let cacheGeneration = 0;
  const api: VideoApi = {
    async getPlaybackUrl(selection, options = {}) {
      const cacheKey = JSON.stringify([selection.brandId, selection.videoId]);
      prunePlaybackUrlCache(playbackUrlCache);
      if (options.forceRefresh) {
        playbackUrlCache.delete(cacheKey);
      } else {
        const cached = playbackUrlCache.get(cacheKey);
        if (cached) return cached;
        playbackUrlCache.delete(cacheKey);
      }

      const requestGeneration = cacheGeneration;
      const path = `/api/brands/${encodeURIComponent(selection.brandId)}/videos/${encodeURIComponent(selection.videoId)}/playback-url`;
      const value = await requestPlayback(
        path,
        options.signal,
        options.fetcher ?? fetcher,
      );
      const playback = parseApiResponse(
        videoPlaybackResponseSchema,
        value,
        "Video playback response has an invalid shape",
      );
      if (requestGeneration !== cacheGeneration) {
        throw new DOMException("Playback request was invalidated", "AbortError");
      }
      if (playback.status === "hosted" && playbackUrlIsFresh(playback)) {
        while (playbackUrlCache.size >= MAXIMUM_CACHED_PLAYBACK_URLS) {
          const oldestCacheKey = playbackUrlCache.keys().next().value;
          if (oldestCacheKey === undefined) break;
          playbackUrlCache.delete(oldestCacheKey);
        }
        playbackUrlCache.set(cacheKey, playback);
      }
      return playback;
    },
  };
  playbackCacheClearers.set(api, () => {
    cacheGeneration += 1;
    playbackUrlCache.clear();
  });
  return api;
}

export const videoApi = createVideoApi();

export function clearVideoPlaybackUrlCache(api: VideoApi = videoApi): void {
  playbackCacheClearers.get(api)?.();
}
